import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sparkles, CheckCircle2, Rocket, Building2, GraduationCap,
  ArrowUpRight, Heart, Clock,
} from "lucide-react";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import SeoHead from "../components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_ICON = {
  decouverte:  Rocket,
  plus:        Sparkles,
  business:    Building2,
  institution: GraduationCap,
};

const PLAN_COLOR = {
  decouverte:  "var(--afr-muted)",
  plus:        "var(--afr-primary)",
  business:    "var(--mod-emploi)",
  institution: "var(--mod-formation)",
};

const FAQ_ITEMS = [
  {
    q: "Pourquoi AFRICTAL est-il gratuit ?",
    a: "Notre mission est de rendre les opportunités accessibles au plus grand nombre. Chaque Africaine et chaque Africain doit pouvoir découvrir la plateforme, chercher des opportunités, se former et être visible — sans barrière financière.",
  },
  {
    q: "Comment fonctionne AFRICTAL Orchestrateur ?",
    a: "AFRICTAL Orchestrateur analyse votre demande, mobilise uniquement les expertises nécessaires et produit un plan d'action personnalisé. La formule gratuite offre un usage limité par mois ; les formules payantes ouvrent une utilisation plus importante et prioritaire.",
  },
  {
    q: "Les abonnements sont-ils obligatoires ?",
    a: "Non. La formule Découverte est intégralement gratuite et couvre déjà l'essentiel : compte, Passeport, offres, missions, formations, messagerie, assistant. Les abonnements sont utiles uniquement quand vos besoins évoluent.",
  },
  {
    q: "Puis-je changer de formule à tout moment ?",
    a: "Oui. Vous pourrez évoluer vers une formule supérieure ou revenir en arrière depuis les paramètres de votre compte, sans engagement long.",
  },
  {
    q: "Quand AFRICTAL Plus sera-t-il disponible ?",
    a: "Les formules payantes sont en cours de finalisation. En attendant, la formule Découverte reste ouverte à tous. Nous vous informerons dès qu'une formule sera prête à être activée.",
  },
];

function PlanCard({ plan }) {
  const Icon = PLAN_ICON[plan.id] || Sparkles;
  const color = PLAN_COLOR[plan.id] || "var(--afr-primary)";
  const highlight = plan.highlight;
  return (
    <article
      className="afr-card afr-price-card"
      data-testid={`pricing-card-${plan.id}`}
      style={{
        borderTop: `4px solid ${color}`,
        background: highlight ? "color-mix(in oklab, var(--afr-primary) 4%, var(--afr-surface))" : "var(--afr-surface)",
        position: "relative",
      }}
    >
      {plan.coming_soon && (
        <span className="afr-badge afr-price-card__soon" data-testid={`pricing-soon-${plan.id}`}>
          <Clock size={11} /> Bientôt disponible
        </span>
      )}

      <div className="afr-persona__icon" style={{ background: `linear-gradient(135deg, ${color}, var(--afr-accent))`, marginBottom: "0.8rem" }}>
        <Icon size={22} />
      </div>

      <h3 style={{ fontFamily: "'Outfit',sans-serif", fontSize: "1.25rem", margin: 0 }}>{plan.name}</h3>
      <p className="afr-muted" style={{ margin: "0.2rem 0 0.6rem", fontSize: "0.86rem" }}>{plan.audience}</p>

      <div style={{ margin: "0.6rem 0 1rem", fontFamily: "'Outfit',sans-serif", fontSize: "1.7rem", fontWeight: 700, color }}>
        {plan.price_label}
      </div>

      <ul className="afr-list" style={{ margin: "0 0 1.2rem", flex: 1 }} data-testid={`pricing-features-${plan.id}`}>
        {(plan.features || []).map((f) => (
          <li key={f}><CheckCircle2 size={13} style={{ verticalAlign: "middle", color, marginRight: 4 }} /> {f}</li>
        ))}
      </ul>

      {plan.is_free ? (
        <Link
          to={plan.cta_href || "/login?mode=signup"}
          className="afr-btn afr-btn--primary"
          data-testid={`pricing-cta-${plan.id}`}
          style={{ marginTop: "auto", justifyContent: "center" }}
        >
          <Rocket size={14} /> {plan.cta_label || "Créer un compte"}
        </Link>
      ) : (
        <button
          type="button"
          className="afr-btn afr-btn--outline"
          disabled
          data-testid={`pricing-cta-${plan.id}`}
          style={{ marginTop: "auto", justifyContent: "center", opacity: 0.8, cursor: "not-allowed" }}
        >
          {plan.cta_label || "Bientôt disponible"}
        </button>
      )}
    </article>
  );
}


export default function Pricing() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/subscriptions/plans`);
        const data = await res.json();
        setPlans(data.items || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="afr-root" data-testid="afr-pricing-root">
      <SeoHead
        title="Tarifs"
        description="Découvrez les formules AFRICTAL adaptées à chaque profil."
        path="/tarifs"
        noIndex
      />
      <PublicNav />

      <div style={{ padding: "1.2rem 2rem 0" }}>
        <nav className="afr-breadcrumb" aria-label="breadcrumb" data-testid="pricing-breadcrumb">
          <Link to="/" data-testid="pricing-crumb-home">Accueil</Link>
          <span className="afr-breadcrumb__sep">/</span>
          <span className="afr-breadcrumb__current">Tarifs</span>
        </nav>
      </div>

      {/* HERO */}
      <header className="afr-hero afr-hero--split" data-testid="pricing-hero">
        <div className="afr-hero__grid">
          <div className="afr-hero__left">
            <span className="afr-eyebrow" style={{ color: "var(--afr-primary)" }}>{t("pricing.hero.eyebrow", "Tarifs AFRICTAL")}</span>
            <h1 className="afr-hero__title" style={{ marginTop: "0.4rem" }}>
              {t("pricing.hero.title", "Commencez gratuitement. Évoluez à votre rythme.")}
            </h1>
            <p className="afr-hero__lead">
              {t("pricing.hero.lead", "Chez AFRICTAL, nous croyons que chacun doit pouvoir accéder aux opportunités. La majorité des fonctionnalités sont accessibles gratuitement. Lorsque vos besoins évoluent, vous pouvez choisir une formule adaptée à votre parcours.")}
            </p>
            <div className="afr-hero__cta-row" style={{ marginTop: "1.6rem", display: "flex", gap: "0.8rem", flexWrap: "wrap" }}>
              <Link to="/login?mode=signup" className="afr-btn afr-btn--primary afr-btn--lg" data-testid="pricing-hero-signup">
                <Rocket size={16} /> Créer un compte gratuitement
              </Link>
              <Link to="/solutions" className="afr-btn afr-btn--outline afr-btn--lg" data-testid="pricing-hero-discover">
                Découvrir AFRICTAL <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          <div className="afr-hero__right" aria-hidden="true">
            <div className="afr-hero-scene" style={{ minHeight: 340 }}>
              <svg viewBox="0 0 480 380" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                <defs>
                  <radialGradient id="priceGlow" cx="50%" cy="45%" r="60%">
                    <stop offset="0%" stopColor="rgba(0,150,64,0.14)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                  </radialGradient>
                </defs>
                <ellipse cx="240" cy="180" rx="220" ry="150" fill="url(#priceGlow)" />
                {[[80,90],[240,60],[400,90],[240,300]].map(([cx,cy],i) => (
                  <g key={i}>
                    <circle cx={cx} cy={cy} r="34" fill="rgba(0,150,64,0.10)" />
                    <circle cx={cx} cy={cy} r="22" fill="var(--afr-primary)" opacity="0.85" />
                  </g>
                ))}
                <line x1="80" y1="90" x2="240" y2="180" stroke="rgba(0,150,64,0.35)" strokeDasharray="4 4" />
                <line x1="240" y1="60" x2="240" y2="180" stroke="rgba(0,150,64,0.35)" strokeDasharray="4 4" />
                <line x1="400" y1="90" x2="240" y2="180" stroke="rgba(0,150,64,0.35)" strokeDasharray="4 4" />
                <line x1="240" y1="300" x2="240" y2="180" stroke="rgba(0,150,64,0.35)" strokeDasharray="4 4" />
                <circle cx="240" cy="180" r="40" fill="var(--afr-secondary)" />
                <text x="240" y="184" textAnchor="middle" fill="#F7F5F0" fontSize="12" fontFamily="Outfit" fontWeight="800">AFRICTAL</text>
              </svg>
            </div>
          </div>
        </div>
      </header>

      {/* PLANS */}
      <section className="afr-section" data-testid="pricing-plans">
        <h2 className="afr-section__title">Les formules AFRICTAL</h2>
        <p className="afr-section__lead">
          Quatre formules simples — une gratuite pour découvrir, trois mensuelles pour aller plus loin.
        </p>
        {loading ? (
          <p className="afr-muted" data-testid="pricing-loading">Chargement des formules…</p>
        ) : (
          <div
            className="afr-grid"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1.2rem", marginTop: "1.4rem", alignItems: "stretch" }}
          >
            {plans.map((p) => <PlanCard key={p.id} plan={p} />)}
          </div>
        )}
      </section>

      {/* POURQUOI GRATUIT */}
      <section className="afr-section" data-testid="pricing-why-free">
        <div
          className="afr-card"
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "1.6rem",
            padding: "2rem",
            background: "linear-gradient(135deg, color-mix(in oklab, var(--afr-primary) 8%, var(--afr-surface)), var(--afr-surface))",
            borderColor: "color-mix(in oklab, var(--afr-primary) 25%, var(--afr-border))",
            borderRadius: 20,
          }}
        >
          <div style={{
            width: 64, height: 64, borderRadius: 16, background: "linear-gradient(135deg, var(--afr-primary), var(--afr-accent))",
            display: "grid", placeItems: "center", color: "#fff",
          }}>
            <Heart size={28} />
          </div>
          <div>
            <span className="afr-eyebrow" style={{ color: "var(--afr-primary)" }}>Notre philosophie</span>
            <h2 style={{ fontFamily: "'Outfit',sans-serif", fontSize: "1.6rem", margin: "0.3rem 0 0.6rem", lineHeight: 1.25 }}>
              Pourquoi une formule gratuite ?
            </h2>
            <p style={{ lineHeight: 1.7, margin: 0, color: "var(--afr-secondary)" }}>
              Notre mission est simple : rendre les opportunités accessibles au plus grand nombre.
              La formule Découverte est intégralement gratuite parce que nous croyons qu'aucun talent africain
              ne devrait rester invisible faute de moyens. Les abonnements payants nous permettent de financer
              le développement de la plateforme et d'offrir des services avancés à celles et ceux qui souhaitent
              aller plus loin dans leur carrière, leurs recrutements ou leurs projets.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="afr-section" data-testid="pricing-faq">
        <h2 className="afr-section__title">Questions fréquentes</h2>
        <div
          className="afr-grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1rem", marginTop: "1rem" }}
        >
          {FAQ_ITEMS.map((it, idx) => (
            <details className="afr-card" key={idx} data-testid={`pricing-faq-${idx}`}>
              <summary style={{
                cursor: "pointer", fontFamily: "'Outfit',sans-serif", fontWeight: 600,
                fontSize: "1.02rem", color: "var(--afr-secondary)",
              }}>
                {it.q}
              </summary>
              <p className="afr-muted" style={{ marginTop: "0.7rem", lineHeight: 1.6 }}>{it.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="afr-section" data-testid="pricing-cta">
        <div className="afr-cta-banner">
          <div className="afr-cta-banner__text">
            <h2>Votre avenir commence aujourd'hui.</h2>
            <p>
              Commencez gratuitement et découvrez comment AFRICTAL peut vous accompagner dans votre carrière,
              vos recrutements ou vos projets.
            </p>
          </div>
          <div className="afr-cta-banner__actions">
            <Link to="/login?mode=signup" className="afr-btn afr-btn--primary afr-btn--lg" data-testid="pricing-cta-signup">
              <Rocket size={16} /> Créer un compte
            </Link>
            <Link
              to="/solutions"
              className="afr-btn afr-btn--outline afr-btn--lg"
              style={{ color: "#F7F5F0", borderColor: "rgba(247,245,240,0.3)" }}
              data-testid="pricing-cta-discover"
            >
              Découvrir AFRICTAL <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
